import { useCallback, useRef, useState } from 'react';
import {
  ApiUrls,
  getAuthHeaders,
  isAgentStreamEvent,
  projectService,
  type AgentStreamEvent,
} from '@ideoz/core';
import { waitForAttachmentsReady } from './waitForAttachmentsReady';

export interface SuggestionStep {
  phase: string;
  label?: string;
}

export type SuggestionStatus = 'pending' | 'adding' | 'added' | 'ignored';

/**
 * The analyze flow is a three-stage pipeline. Only the first stage has
 * countable units (files); the other two are indeterminate waits — so we track
 * *which* stage is active rather than a fake percentage.
 */
export type AnalyzeStage = 'upload' | 'indexing' | 'analyzing';

export interface ScopeSuggestionItem {
  id: string;
  name: string;
  description: string;
  status: SuggestionStatus;
}

type Phase = 'idle' | 'analyzing' | 'done' | 'error';

/**
 * The attachment-upload response shape is not stable: a single upload returns
 * `{ data: { attachment_id, ... } }`, multiple returns `{ data: [...] }` or
 * `{ files: [...] }`, and the id key is `attachment_id` — not `id`. Read every
 * shape so a missed id can't silently send an empty `attachment_ids`, which the
 * API 400s on (`attachment_ids must not be empty`).
 */
const extractAttachmentIds = (res: any): string[] => {
  const body = res?.data ?? res;
  const items = Array.isArray(body) ? body : (body?.files ?? [body]);
  return items
    .map((item: any) => item?.attachment_id ?? item?.attachmentId ?? item?.id)
    .filter(Boolean)
    .map(String);
};

/**
 * Bulk scope suggestion flow: upload messy files → stream AI status steps and
 * suggested scopes → the user approves (create via the existing scope API) or
 * ignores each one.
 */
export function useScopeSuggestions(startupId: string) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [stage, setStage] = useState<AnalyzeStage | null>(null);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [steps, setSteps] = useState<SuggestionStep[]>([]);
  const [suggestions, setSuggestions] = useState<ScopeSuggestionItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const reset = useCallback(() => {
    setPhase('idle');
    setStage(null);
    setUploadedCount(0);
    setSteps([]);
    setSuggestions([]);
    setError(null);
  }, []);

  const analyze = useCallback(
    async (files: File[]) => {
      if (!startupId || files.length === 0) return;
      setPhase('analyzing');
      setStage('upload');
      setUploadedCount(0);
      setSteps([]);
      setSuggestions([]);
      setError(null);

      // 1. Persist the files as startup attachments; collect ids. Upload in
      // parallel — the files are independent, so serialising them only made the
      // slowest phase slower. Bump the counter as each resolves so the UI can
      // show real per-file progress instead of a stalled global bar.
      const attachmentIds: string[] = [];
      try {
        const responses = await Promise.all(
          files.map((file) => {
            const fd = new FormData();
            fd.append('file', file);
            return projectService.uploadProjectFiles(startupId, fd).then((res: any) => {
              setUploadedCount((c) => c + 1);
              return res;
            });
          }),
        );
        for (const res of responses) attachmentIds.push(...extractAttachmentIds(res));
      } catch (err) {
        console.error('Failed to upload scope files', err);
        setError('Failed to upload files.');
        setPhase('error');
        return;
      }

      // The API rejects an empty list, and an empty filter would silently widen
      // retrieval to the whole project — so an upload that gave us back no
      // usable ids is a hard error, not a request we should send.
      if (attachmentIds.length === 0) {
        console.error('Upload returned no attachment ids for scope suggestion');
        setError('Could not read the uploaded files. Please try again.');
        setPhase('error');
        return;
      }

      // 1b. Wait for indexing. The upload returns before Celery has embedded
      // the file, so asking for suggestions right away searches vectors that
      // don't exist yet and comes back with nothing.
      setStage('indexing');
      const readiness = await waitForAttachmentsReady(attachmentIds);
      if (readiness.status !== 'ready') {
        console.error('Attachments never became ready', readiness);
        setError(
          readiness.status === 'failed'
            ? 'We could not process some of your files. Please try uploading them again.'
            : 'Your files are taking longer than expected to process. Please try again in a moment.',
        );
        setPhase('error');
        return;
      }

      // 2. Stream suggestions from the AI (direct FE→Flask SSE).
      setStage('analyzing');
      try {
        const response = await fetch(ApiUrls.suggestScopes(startupId), {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
            Accept: 'text/event-stream, application/json',
          },
          body: JSON.stringify({ attachment_ids: attachmentIds }),
        });
        if (!response.ok || !response.body) {
          throw new Error(`suggest failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const handle = (evt: AgentStreamEvent) => {
          if (evt.type === 'status') {
            setSteps((prev) => [...prev, { phase: evt.payload.phase, label: evt.payload.label }]);
          } else if (evt.type === 'scope_suggestions') {
            setSuggestions(
              evt.payload.scopes.map((s) => ({
                id: `scope-${seq.current++}`,
                name: s.name,
                description: s.description,
                status: 'pending' as const,
              })),
            );
          } else if (evt.type === 'error') {
            setError(evt.payload.message || 'Something went wrong.');
          }
        };

        const processLine = (line: string) => {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) return;
          const raw = trimmed.slice(5).trim();
          if (!raw) return;
          try {
            const parsed = JSON.parse(raw);
            if (isAgentStreamEvent(parsed)) handle(parsed);
          } catch {
            /* ignore keep-alives / partial lines */
          }
        };

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) processLine(line);
        }
        if (buffer.trim()) processLine(buffer);

        setPhase((p) => (p === 'error' ? 'error' : 'done'));
      } catch (err) {
        console.error('Scope suggestion stream failed', err);
        setError('Failed to analyze files for scopes.');
        setPhase('error');
      }
    },
    [startupId],
  );

  const ignore = useCallback((id: string) => {
    setSuggestions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: 'ignored' } : s)),
    );
  }, []);

  const add = useCallback(
    async (id: string): Promise<string | undefined> => {
      const target = suggestions.find((s) => s.id === id);
      if (!target || target.status !== 'pending') return undefined;
      setSuggestions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status: 'adding' } : s)),
      );
      try {
        const created = await projectService.createScope(startupId, {
          name: target.name,
          description: target.description,
        });
        setSuggestions((prev) =>
          prev.map((s) => (s.id === id ? { ...s, status: 'added' } : s)),
        );
        // Return the new scope's id so the caller can queue an evaluation.
        return created?.id;
      } catch (err) {
        console.error('Failed to create scope', err);
        setSuggestions((prev) =>
          prev.map((s) => (s.id === id ? { ...s, status: 'pending' } : s)),
        );
        return undefined;
      }
    },
    [startupId, suggestions],
  );

  return {
    phase,
    stage,
    uploadedCount,
    steps,
    suggestions,
    error,
    analyze,
    ignore,
    add,
    reset,
  };
}
